/* src/components/pages/Informefinancieroenru.jsx - Modificado el 07-02-2026 */
/* ------- consulta informe financiero enrolado para carpetas subidas ------- */

import React, { useState, useEffect } from 'react';
import { TextField, Box, Button, Typography, Stack, MenuItem } from '@mui/material';
import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { CustomToolbar } from '../../components/datagrid/CustomToolbar';
import DataGridSkeleton from '../../components/datagrid/DataGridSkeleton';

import { RiFileSearchLine, RiFilePdf2Fill, RiFileTextLine } from 'react-icons/ri';

import { useAuth } from '../../context/AuthContext';

import { useFeedback } from '../../context/FeedbackContext';

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";

const Informefinancieroenru = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* Estados paginación server */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolados, setEnrolados] = useState([]);
  const [rutSeleccionado, setRutSeleccionado] = useState('');

  /* Generar informe financiero */
  const generarInforme = async (row) => {
    let pdfWindow = null;

    try {
      const RutCli = row?.rutcli;
      if (!RutCli) throw new Error('Rut cliente no disponible');

      const esCarpeta = row.tipo === 1 || row.tipo === 2;

      const Tipocar = esCarpeta ? 'carpeta' : 'renta';
      const Ftipo = row.tipo === 1 ? 1 : row.tipo === 2 ? 2 : 0;

      pdfWindow = window.open('', '_blank');
      if (!pdfWindow || pdfWindow.closed) {
        showFeedback({
          message:
            'Debe habilitar las ventanas emergentes para ver el informe financiero.',
          severity: 'warning',
        });
        return;
      }

      pdfWindow.document.title = 'Generando informe financiero...';
      pdfWindow.document.body.innerHTML = `
        <style>
          body {
            background:#0f172a;
            color:#e5e7eb;
            font-family:Segoe UI, sans-serif;
            display:flex;
            align-items:center;
            justify-content:center;
            height:100vh;
            margin:0;
          }
        </style>
        <h2>Generando informe financiero…</h2>
      `;

      const response = await api.post('/subircarpeta/getpdfinforme', {
        RutCli,
        RutEnr: '',
        Tipocar,
        Ftipo,
      });

      const pdfBlob = new Blob([response.data], {
        type: 'application/pdf',
      });

      pdfWindow.location.href = URL.createObjectURL(pdfBlob);
    } catch (err) {
      if (pdfWindow && !pdfWindow.closed) pdfWindow.close();

      showFeedback({
        message:
          'No fue posible obtener su solicitud, por favor intente más tarde',
        severity: 'error',
      });
    }
  };

  /* cargar enrolados asociados al cliente */
  useEffect(() => {
    (async () => {
      try {
        const RutCli = normalizeRut(user.usuario);

        const response = await api.post('/enrolados/enroladosasociados', {
          RutCli,
          RutEnr: '',
          Tipousr: 1, /* Cliente */
        });

        if (response.data.success && response.data.rows) {
          setEnrolados(response.data.rows);
        } else {
          setEnrolados([]);

          showFeedback({
            message: 'No se encontraron registros para los parámetros ingresados',
            severity: 'warning',
          });
        }
      } catch (error) {
        showFeedback({
          message: 'No fue posible obtener su solicitud, por favor intente más tarde',
          severity: 'error',
        });
      }
    })();
  }, [showFeedback, user.usuario]);

  /* Validar rut del enroaldo */
  const rutEnrValido = React.useMemo(() => {
    if (!rutSeleccionado) return false;
    return validarRut(rutSeleccionado);
  }, [rutSeleccionado]);


  /* Buscar carpetas (server)  */
  const fetchCarpetas = async ({ page = 0, pageSize = 20 } = {}) => {
    if (!user.usuario) {
      showFeedback({
        message: 'Debe ingresar el Rut',
        severity: 'warning',
      });
      return;
    }
    if (!rutEnrValido) {
      setRows([]);
      setRowCount(0);
      showFeedback({
        message: "El Rut del enrolado no es válido",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const response = await api.post('/subircarpeta/getcarpetas', {
        RutCli,
        RutEnr: '',
        Tipousr: 1, /* Cliente */
        page,
        pageSize,
      });

      if (response.data?.success) {
        setRows(response.data.rows || []);
        setRowCount(response.data.total || 0);
      }
    } catch {
      showFeedback({
        message:
          'No fue posible obtener su solicitud, por favor intente más tarde',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };


  /* Columnas DataGrid */
  const columns = [
    {
      field: 'pdf',
      headerName: 'PDF',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,

      renderCell: (params) => {
        const { id } = params.row;

        const handleOpenPDF = async () => {
          if (!id) return;

          try {
            const response = await api.post('/subircarpeta/getcarpetapdf',
              { id },
              { responseType: 'blob' }
            );

            const pdfBlob = new Blob([response.data], {
              type: 'application/pdf',
            });

            const url = window.URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');

            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);

          } catch (error) {
            showFeedback({
              message: 'No fue posible obtener el Pdf',
              severity: 'error',
            });
          }
        };

        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              aria-label="Abrir PDF"
              onClick={handleOpenPDF}
              sx={{
                backgroundColor: (theme) => theme.custom.ui.primaryAction,
                color: (theme) => theme.custom.ui.primaryActionText,
                minWidth: 0,
                p: 0,
                width: 32,
                height: 24,
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.custom.ui.primaryActionHover,
                },
              }}
            >
              <RiFilePdf2Fill size={20} />
            </Button>
          </Box>
        );
      },
    },

    {
      field: 'acciones',
      headerName: 'Informe',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,

      renderCell: (params) => {
        const row = params.row;

        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              onClick={() => generarInforme(row)}
              disabled={!row.fecha}
              sx={{
                backgroundColor: (theme) => theme.custom.ui.primaryAction,
                color: (theme) => theme.custom.ui.primaryActionText,
                minWidth: 0,
                p: 0,
                width: 32,
                height: 24,
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.custom.ui.primaryActionHover,
                },
              }}
            >
              <RiFileTextLine size={20} />
            </Button>
          </Box>
        );
      },
    },
    {
      field: 'fecha',
      headerName: 'Fecha Upload',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return '—';

        const fecha = new Date(params.value);

        const fechaFormateada = fecha.toLocaleString('es-CL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });

        /* Resultado: 2025-10-08 13:59:55 */
        return (
          <Box sx={{ fontSize: '0.75rem', textAlign: 'center', width: '100%' }}>
            {fechaFormateada.replace(',', '')}
          </Box>
        );
      },
    },
    {
      field: 'tipo',
      headerName: 'Tipo Carpeta',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: ({ value }) =>
        Number(value) === 1 ? 'Créditos' : 'Personalizada',
    },
    {
      field: 'rutcli',
      headerName: 'Rut Contribuyente',
      width: 180,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'nombre',
      headerName: 'Razón Social',
      width: 320,
    },
  ];

  /* Render */
  return (
    <Box sx={{ width: '100%' }}>
      {/* Controles */}
      <Stack direction="row" spacing={1} mb={1}>
        <TextField
          label="Rut"
          value={user?.usuario || ''}
          size="small"
          disabled
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ''}
          size="small"
          disabled
          sx={{ flexGrow: 1 }}
        />

        {/* Selector de enrolados */}
        <TextField
          select
          id="enrolado-selector"
          label="Enrolado"
          value={rutSeleccionado}
          size="small"
          onChange={(e) => {
            const rut = e.target.value;
            setRutSeleccionado(rut);
            setRows([]);
            setRowCount(0);
            setPage(0);
          }}
          variant="outlined"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay registros</MenuItem>
          )}
        </TextField>

        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={() => {
            setPage(0);
            fetchCarpetas({ page: 0, pageSize });
          }}
        >
          Buscar
        </Button>
      </Stack>

      {/* DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 210px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}

          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);

            fetchCarpetas({
              page: finalPage,
              pageSize: newPageSize,
            });
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString('es-CL')} registros encontrados
      </Typography>
    </Box>
  );
};

export default Informefinancieroenru;
